import {
	DEFAULT_BAR_STRETCH_BOUNCE_MS,
	resolveBufferedEndPercent,
	resolveBufferRevealPrepareDurationMs,
	resolvePlaybackBufferThresholdPercent,
} from "../waveform-bar-visual";
import {
	INTERACTIVE_AUDIO_MIN_BUFFER_SECONDS,
	INTERACTIVE_AUDIO_MIN_REVEAL_DURATION_MS,
	INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT,
} from "./constants";

export {
	INTERACTIVE_AUDIO_MIN_BUFFER_SECONDS,
	INTERACTIVE_AUDIO_MIN_REVEAL_DURATION_MS,
};

export function resolveInteractiveAudioBufferThresholdPercent(
	audio: HTMLAudioElement,
): number {
	return resolvePlaybackBufferThresholdPercent(
		audio.duration,
		INTERACTIVE_AUDIO_MIN_BUFFER_SECONDS,
	);
}

export function waitForAudioDuration(audio: HTMLAudioElement): Promise<void> {
	if (Number.isFinite(audio.duration) && audio.duration > 0) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const finish = () => {
			cleanup();
			resolve();
		};

		const check = () => {
			if (Number.isFinite(audio.duration) && audio.duration > 0) {
				finish();
			}
		};

		const cleanup = () => {
			audio.removeEventListener("loadedmetadata", check);
			audio.removeEventListener("durationchange", check);
			audio.removeEventListener("canplay", check);
		};

		audio.addEventListener("loadedmetadata", check);
		audio.addEventListener("durationchange", check);
		audio.addEventListener("canplay", check);
		check();
	});
}

export function waitForAudioBufferThreshold(
	audio: HTMLAudioElement,
	thresholdPercent: number,
): Promise<void> {
	return new Promise((resolve) => {
		const finish = () => {
			cleanup();
			resolve();
		};

		const check = () => {
			if (resolveBufferedEndPercent(audio) >= thresholdPercent - 0.25) {
				finish();
			}
		};

		const cleanup = () => {
			audio.removeEventListener("progress", check);
			audio.removeEventListener("loadeddata", check);
			audio.removeEventListener("canplay", check);
			audio.removeEventListener("canplaythrough", check);
		};

		audio.addEventListener("progress", check);
		audio.addEventListener("loadeddata", check);
		audio.addEventListener("canplay", check);
		audio.addEventListener("canplaythrough", check);
		check();
	});
}

export function waitForRevealDisplayCatchUp(
	readDisplayPercent: () => number,
	readActualPercent: () => number,
	minRevealDurationMs: number,
	readSessionStartMs: () => number,
	options: {
		barCount?: number;
		barStretchMs?: number;
	} = {},
	maxFrames = 900,
): Promise<void> {
	const {
		barCount = INTERACTIVE_AUDIO_WAVEFORM_BAR_COUNT,
		barStretchMs = DEFAULT_BAR_STRETCH_BOUNCE_MS,
	} = options;
	const requiredDurationMs = resolveBufferRevealPrepareDurationMs(
		minRevealDurationMs,
		barCount,
		barStretchMs,
	);

	return new Promise((resolve) => {
		let frames = 0;
		const tick = () => {
			const actual = readActualPercent();
			const elapsed = performance.now() - readSessionStartMs();
			const display = readDisplayPercent();
			const minDurationMet = elapsed >= requiredDurationMs;

			if (
				(display >= actual - 0.25 && minDurationMet) ||
				frames >= maxFrames
			) {
				resolve();
				return;
			}
			frames += 1;
			requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
	});
}
